import { Box, Typography } from '@mui/material';
import Pill from '../common/Pill';
import { SP } from '../../theme/tokens';

/**
 * BundleCostProvenance — the ONE line that names where a bundle's cost came from.
 *
 * @description
 *   Renders the SOURCE of the rolled-up cost shown in the tile above it:
 *   `Sum of components` (Σ of the components, the primary source) or
 *   `Own cost (fallback)` (the bundle variant's own `cost_per_item`, used ONLY
 *   because the component sum is incomplete). The value comes from `cost_source`
 *   on `GET /api/products/bundle/<product_id>/components`.
 *
 *   It exists because the number alone lies by omission. The same tile shows
 *   `12,50 €` whether that is a sum of components or the parent's own fallback
 *   cost — two different facts with different consequences for the price
 *   automation. The OWNER decision of 2026-10-08 fixed the order (sum first,
 *   own cost second); a surface that shows the second source as if it were the
 *   first contradicts the number beside it.
 *
 *   🔴 **The line names the SOURCE, the tile names the REASON** (R16 W1/W4 and
 *   R15 NIT 3, 2026-10-09). `StatTile`'s hint already carries `describeBundleCost`'s
 *   `note` — the reason ("Every component carries a cost", "The component sum is
 *   incomplete"). This line therefore carries NO second sentence: before the
 *   split both printed the source, `the component sum is incomplete` stood
 *   twice within 40 px, and the only new information here was the word
 *   "(fallback)".
 *
 *   🔴 **No source, no line.** When the cost is unknown — because no component
 *   carries a cost, or because the variants carry different component sets —
 *   this component renders NOTHING. Both cases are already fully stated by the
 *   tile ("Unknown" / "Varies by variant" plus its reason), and a line here
 *   would be wrong rather than merely redundant:
 *   · "Unknown" would stand twice under one another (R15 NIT 3), and
 *   · "no usable cost basis" would be too broad for the variants case — the
 *     basis exists, it is only not the same one per variant (R16 W4).
 *   `pattern-library.md` §"lieber keine Erklärung als eine falsche" is the
 *   house rule for exactly this trade.
 *
 *   🔴 `own` is labelled as a FALLBACK, in those words. Not "Own cost" alone:
 *   the reader must be able to tell it apart from the primary source without
 *   reading the code.
 *   🔴 The wording carries the meaning, not the colour (Rule 22 No. 2): the
 *   `Pill` tone is a second coding, never the only one.
 *
 * @useWhen
 *   - Next to a bundle's rolled-up cost tile, on the bundle detail page
 *   - The products-list SlidePanel cost footer (`ProductsTable`), since
 *     2026-10-09 — that footer prints `describeBundleCost`'s `note` too
 *   - Any surface that shows a bundle cost and must say where it came from
 *
 * @dontUseFor
 *   - The per-component line costs — that is BundleComponentList
 *   - A per-product provenance (verified/asserted claims) — that is ProvenanceMark
 *
 * @related
 *   - BundleComponentList — owns `describeBundleCost`, the value AND its reason
 *   - StatTile — the tile this line belongs to (label "Rolled-up cost"); it
 *     prints `note`, this line prints `source`
 *   - Pill — the chip shape for the label
 *
 * @reuseHints
 *   - A future bundle editor preview, where the source explains the number
 *
 * @props
 *   costSource  string?  'components' | 'own'; any other value renders NOTHING
 *                        (`unknown`, `null`, `undefined` and typos alike) —
 *                        there is no source to name, and the tile beside it
 *                        already says what is known.
 */
const HERKUNFT = {
  components: { label: 'Sum of components', tone: 'ok' },
  own: { label: 'Own cost (fallback)', tone: 'warn' },
};

export default function BundleCostProvenance({ costSource }) {
  // Die zwei NAMEN, nicht ein Index in ein Objekt: `HERKUNFT['toString']` waere
  // sonst eine geerbte Funktion und damit „wahr" — der Baustein rendert bei
  // jedem unbekannten Wert nichts, und das soll er auch bei einem Erbe tun.
  const cfg = (costSource === 'components' || costSource === 'own')
    ? HERKUNFT[costSource]
    : null;
  if (!cfg) return null;

  return (
    <Box
      role="group"
      aria-label="Cost basis"
      sx={{ display: 'flex', alignItems: 'center', gap: SP.chip, flexWrap: 'wrap' }}
    >
      <Typography variant="caption" color="text.secondary">
        Cost basis:
      </Typography>
      <Pill tone={cfg.tone}>{cfg.label}</Pill>
    </Box>
  );
}
